import { defineStore } from 'pinia';
import { useUserStore } from 'src/core/stores/user';
import FarmPlotsIcon from '@/assets/icons/farm-plot-icon.svg?component';
import { NavigationItemGroup } from 'src/core/types/app';
import { useApiStore } from 'src/core/stores/api';
import { useDatasetEnumsStore } from 'src/core/stores/enum/dataset';
import { useShipmentEnumsStore } from 'src/core/stores/enum/shipment';
import routes from 'src/router/routes';
import { useUserEnumsStore } from 'src/core/stores/enum/user';
import { useAnalyticsStore } from 'src/core/stores/analytics';
import { useExportEnumsStore } from 'src/core/stores/enum/export';
import { useDataPreparationEnumsStore } from 'src/core/stores/enum/dataPreparation';
import { usePermissionStore } from 'src/core/stores/permission';
import { useBillingStore } from 'src/modules/usage/stores/billing';
import {
  showIntercom,
  isIntercomOpen,
  isIntercomAvailable,
} from 'src/core/services/intercom';
import { markRaw } from 'vue';

type AppStore = {
  initialized: boolean;
  loading: boolean;
  errorDetails?: {
    message: string;
    type: string;
  };
  navigationItemsGroup: NavigationItemGroup[];
  unAuthorizedlist: string[];
  t: any;
};

/**
 * Represents the application store.
 */
export const useAppStore = defineStore('app', {
  state: () =>
    ({
      initialized: false,
      loading: false,
      navigationItemsGroup: [],
      unAuthorizedlist: [],
      errorDetails: undefined,
      t: null,
    } as AppStore),

  getters: {
    /**
     * Custom getter for accessing the route.
     * @returns {boolean} The route authorised access.
     */
    isRouteAuthorized: (state) => (routeName: string) => {
      return !state.unAuthorizedlist.includes(routeName);
    },
  },

  actions: {
    /**
     * Initializes the application store. App.vue shows its loader while `loading` is true.
     */
    async initialize(): Promise<boolean> {
      this.loading = true;
      const apiStore = useApiStore();
      const userStore = useUserStore();
      const analyticsStore = useAnalyticsStore();
      await analyticsStore.initialize();
      apiStore.createRequestInstance();
      if (userStore.access_token) {
        await userStore.getUserInfo();
        userStore.startConsumptionPolling();
        await this.getAllEnums();
      }
      await this.initializeNavigationItems();
      await this.fillUnAuthorizedList();
      await analyticsStore.ready();
      this.loading = false;
      this.initialized = true;
      return true;
    },

    /**
     * Fills the unAuthorizedlist array with the names of the routes that are not authorized to be shown or accessed.
     */
    fillUnAuthorizedList() {
      const permissionStore = usePermissionStore();
      const userStore = useUserStore();
      this.unAuthorizedlist = [];
      routes.forEach((route) => {
        if (route.meta?.disabled) {
          this.unAuthorizedlist.push(route.name as string);
        } else if (route.meta?.permission_to_access) {
          if (
            !permissionStore.checkUserAccess(
              route.meta.permission_to_access as string
            )
          ) {
            this.unAuthorizedlist.push(route.name as string);
          }
        } else if (route.meta?.permission_to_access_fnc) {
          if (
            !route.meta?.permission_to_access_fnc(
              userStore.user,
              permissionStore.checkUserAccess
            )
          ) {
            this.unAuthorizedlist.push(route.name as string);
          }
        }
      });
    },

    /**
     * Initializes the navigation items.
     */
    async initializeNavigationItems() {
      this.navigationItemsGroup = [
        // Data Preparation: headerless group at the top (VP-2959)
        {
          items: [
            {
              name: 'Data Preparation',
              route: { name: 'dataPreparationList' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'science',
              },
              isVisible: () => {
                const userStore = useUserStore();
                return userStore.user?.is_data_preparation_enabled === true;
              },
            },
          ],
          position: 'normal',
        },
        // Section 1: Verify & analyse
        {
          name: 'Verify & analyse',
          sectionKey: 'app.sidebar.section_verify',
          items: [
            {
              name: 'Datasets',
              route: { name: 'datasets' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'data_table',
              },
              action: {
                icon: 'add',
                label: 'ADD',
                route: { name: 'datasetCreate' },
              },
            },
            {
              name: 'Results',
              route: { name: 'results' },
              icon: {
                type: 'material-icons-outlined',
                value: 'table_chart',
              },
            },
            {
              name: 'Farm plots',
              route: { name: 'farmPlots' },
              icon: {
                type: 'component',
                value: markRaw(FarmPlotsIcon),
              },
            },
          ],
          position: 'normal',
        },
        // Section 2: Operationalise
        {
          name: 'Operationalise',
          sectionKey: 'app.sidebar.section_operationalise',
          items: [
            {
              name: 'Shipments',
              route: { name: 'shipments' },
              icon: {
                type: 'material-icons-outlined',
                value: 'inventory_2',
              },
            },
            {
              name: 'Vault',
              route: { name: 'vault' },
              icon: {
                type: 'material-icons-outlined',
                value: 'lock_open',
              },
            },
          ],
          position: 'normal',
        },
        // Section 3: Learn & get help
        {
          name: 'Learn & get help',
          sectionKey: 'app.sidebar.section_learn',
          items: [
            {
              name: 'Tests & resolutions',
              route: { name: 'Tests & Resolutions' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'lab_panel',
              },
            },
            {
              name: 'Get help',
              onClick: showIntercom,
              isActive: () => isIntercomOpen.value,
              isVisible: () => isIntercomAvailable.value,
              icon: {
                type: 'material-icons-outlined',
                value: 'contact_support',
              },
            },
          ],
          position: 'normal',
        },
        // Section 4: Manage
        {
          name: 'Manage',
          sectionKey: 'app.sidebar.section_manage',
          items: [
            {
              name: 'Usage analytics',
              route: { name: 'usageAnalytics' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'monitoring',
              },
            },
            {
              name: 'Users',
              route: { name: 'userAccess' },
              icon: {
                type: 'material-icons-outlined',
                value: 'person',
              },
            },
            {
              name: 'Suppliers',
              route: { name: 'suppliers' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'trolley',
              },
            },
            {
              name: 'Customers',
              route: { name: 'customers' },
              icon: {
                type: 'material-icons-outlined',
                value: 'apartment',
              },
            },
            {
              name: 'API access',
              route: { name: 'apiAccess' },
              icon: {
                type: 'material-icons',
                value: 'generating_tokens',
              },
            },
            {
              name: 'Account settings',
              route: { name: 'userSettings' },
              icon: {
                type: 'material-icons-outlined',
                value: 'settings',
              },
            },
            {
              name: 'Diagnostics',
              route: { name: 'diagnostic-v2' },
              icon: {
                type: 'material-icons-outlined',
                value: 'construction',
              },
            },
            {
              name: 'Scoring Inspector',
              route: { name: 'scoringInspector' },
              icon: {
                type: 'material-symbols-outlined',
                value: 'rule',
              },
            },
          ],
          position: 'normal',
        },
      ];
    },

    /**
     * Retrieves all the enums from different stores.
     * @returns {Promise<void>} A promise that resolves when all the enums are retrieved.
     */
    async getAllEnums() {
      const datasetEnumsStore = useDatasetEnumsStore();
      await datasetEnumsStore.getEnums();
      const userEnumsStore = useUserEnumsStore();
      await userEnumsStore.getEnums();
      const ExportEnumsStore = useExportEnumsStore();
      await ExportEnumsStore.getEnums();
      const shipmentEnumsStore = useShipmentEnumsStore();
      await shipmentEnumsStore.getEnums();
      // The backend answers this endpoint with a 403 for customers without the
      // Data Preparation feature flag, so only users who have it load the enums.
      const userStore = useUserStore();
      if (userStore.user?.is_data_preparation_enabled === true) {
        const dataPreparationEnumsStore = useDataPreparationEnumsStore();
        await dataPreparationEnumsStore.getEnums();
      }

      const billingStore = useBillingStore();

      const permissionStore = usePermissionStore();
      if (
        permissionStore.checkUserAccess('app.billing.api:read_pricing_plans')
      ) {
        await billingStore.getBillingPlans();
      }
    },
  },
});
